import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import api from "../lib/api";
import { Users, ShieldCheck, ArrowRight, Loader2, Clock, Hourglass } from "lucide-react";

export default function RoleSelectionPage() {
    const { user, loading } = useAuth();
    const company = useCompany();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [presenzeEnabled, setPresenzeEnabled] = useState(false);

    useEffect(() => {
        api.get("/presenze/config")
            .then((r) => setPresenzeEnabled(!!r.data.enabled))
            .catch(() => setPresenzeEnabled(false));
    }, []);

    // Deep-link support: /accesso?role=admin or /accesso?role=employee
    useEffect(() => {
        const role = params.get("role");
        if (role === "admin" || role === "employee") {
            navigate(`/login?role=${role}`, { replace: true });
        }
    }, [params, navigate]);

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center" data-testid="auth-loading">
                <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
            </div>
        );
    }
    if (user) {
        // Operators (dipendenti-amministratori) can use BOTH areas: let them
        // choose the entry instead of auto-redirecting.
        if (user.role !== "operator") {
            const target = user.role === "admin" ? "/" : "/portale";
            return <Navigate to={target} replace />;
        }
    }

    return (
        <div
            className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#1F3D6B] via-[#376DB3] to-[#5BAEDB]"
            data-testid="role-selection-page"
        >
            {/* Decorative noise/grain overlay */}
            <div
                className="absolute inset-0 opacity-[0.07]"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                }}
            />
            {/* Decorative circles */}
            <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-[#5BAEDB]/30 blur-3xl" />

            <div className="relative z-10 flex min-h-screen flex-col">
                {/* Header */}
                <header className="flex items-center justify-between gap-2 px-4 py-6 sm:px-10">
                    <div className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.12em] text-white/70 sm:text-xs sm:tracking-[0.2em]" data-testid="role-portal-header">
                        {company.ragione_sociale}
                    </div>
                    <div className="text-right text-xs text-white/80">
                        <div>Per assistenza:</div>
                        <a
                            href="mailto:dipendenti@splendorsud.com"
                            className="font-medium text-white hover:underline"
                            data-testid="role-support-email"
                        >
                            dipendenti@splendorsud.com
                        </a>
                    </div>
                </header>

                {/* Main */}
                <main className="flex flex-1 items-center justify-center px-4 py-8 sm:px-10">
                    <div className="w-full max-w-5xl">
                        <div className="mb-10 flex flex-col items-center text-center">
                            {/* Large centered logo */}
                            <div className="mb-6 inline-flex rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-white/30">
                                <img
                                    src="/brand/logo-full.jpg"
                                    alt="Splendor Sud"
                                    width={344}
                                    height={128}
                                    className="h-24 w-auto sm:h-32"
                                    style={{ aspectRatio: "1812 / 674" }}
                                />
                            </div>
                            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-white/70">
                                Benvenuto
                            </div>
                            <h1 className="mt-2 font-display text-5xl font-bold leading-tight text-white sm:text-6xl lg:text-7xl" data-testid="role-page-portal-title">
                                PORTALE HR
                            </h1>
                            <p className="mt-3 text-lg font-medium text-white/90 sm:text-xl" data-testid="role-page-heading">
                                Come vuoi accedere?
                            </p>
                            <p className="mx-auto mt-3 max-w-xl text-sm text-white/80 sm:text-base">
                                Seleziona il tipo di utente per continuare. Ogni profilo ha funzioni
                                dedicate.
                            </p>
                        </div>

                        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {presenzeEnabled ? (
                                <Link
                                    to="/login?role=timbratura"
                                    className="group relative overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-white/20 transition-all hover:-translate-y-1 hover:shadow-[0_20px_60px_-10px_rgba(55,109,179,0.5)]"
                                    data-testid="role-card-timbratura"
                                >
                                    <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#376DB3] to-[#5BAEDB]" />
                                    <div className="p-7 sm:p-8">
                                        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#376DB3]/10 text-[#376DB3] transition-colors group-hover:bg-[#376DB3] group-hover:text-white">
                                            <Clock className="h-7 w-7" />
                                        </div>
                                        <div className="mt-5">
                                            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                                                Presenze
                                            </div>
                                            <h2 className="mt-1 font-display text-2xl font-bold text-slate-900">
                                                Timbratura
                                            </h2>
                                            <p className="mt-2 text-sm text-slate-600">
                                                Registra l'entrata e l'uscita dal tuo turno di lavoro in
                                                modo semplice e veloce.
                                            </p>
                                        </div>
                                        <ul className="mt-5 space-y-1.5 text-xs text-slate-500">
                                            <li>• Timbra entrata e uscita dal telefono</li>
                                            <li>• Posizione GPS verificata sul cantiere</li>
                                            <li>• Accesso con le credenziali del portale</li>
                                        </ul>
                                        <div className="mt-6 inline-flex items-center gap-2 font-medium text-[#376DB3] transition-all group-hover:gap-3">
                                            Vai alla timbratura
                                            <ArrowRight className="h-4 w-4" />
                                        </div>
                                    </div>
                                </Link>
                            ) : (
                            <div
                                className="relative overflow-hidden rounded-2xl bg-white/95 shadow-2xl ring-1 ring-white/20"
                                data-testid="role-card-timbratura"
                            >
                                <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#376DB3] to-[#5BAEDB] opacity-60" />
                                <div className="p-7 sm:p-8">
                                    <div className="flex items-start justify-between">
                                        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#376DB3]/10 text-[#376DB3]/70">
                                            <Clock className="h-7 w-7" />
                                        </div>
                                        <span
                                            className="rounded-full bg-[#376DB3]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#376DB3] ring-1 ring-[#376DB3]/25"
                                            data-testid="timbratura-coming-soon-badge"
                                        >
                                            Presto disponibile
                                        </span>
                                    </div>
                                    <div className="mt-5">
                                        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                                            Presenze
                                        </div>
                                        <h2 className="mt-1 font-display text-2xl font-bold text-slate-700">
                                            Timbratura
                                        </h2>
                                        <p className="mt-2 text-sm text-slate-600">
                                            Registra l'entrata e l'uscita dal tuo turno di lavoro in
                                            modo semplice e veloce.
                                        </p>
                                    </div>
                                    <ul className="mt-5 space-y-1.5 text-xs text-slate-500">
                                        <li>• Timbra entrata e uscita dal telefono</li>
                                        <li>• Orario registrato in automatico</li>
                                        <li>• Accesso con le credenziali del portale</li>
                                    </ul>
                                    <div className="mt-6 inline-flex items-center gap-2 font-medium text-slate-400">
                                        <Hourglass className="h-4 w-4" />
                                        La funzione non è ancora disponibile
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* Employee card */}
                            <Link
                                to="/login?role=employee"
                                className="group relative overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-white/20 transition-all hover:-translate-y-1 hover:shadow-[0_20px_60px_-10px_rgba(55,109,179,0.5)]"
                                data-testid="role-card-employee"
                            >
                                <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#376DB3] to-[#5BAEDB]" />
                                <div className="p-7 sm:p-8">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#376DB3]/10 text-[#376DB3] transition-colors group-hover:bg-[#376DB3] group-hover:text-white">
                                        <Users className="h-7 w-7" />
                                    </div>
                                    <div className="mt-5">
                                        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                                            Area personale
                                        </div>
                                        <h2 className="mt-1 font-display text-2xl font-bold text-slate-900">
                                            Sono un dipendente
                                        </h2>
                                        <p className="mt-2 text-sm text-slate-600">
                                            Accedi al tuo portale personale per scaricare buste paga,
                                            CU, contratti e comunicazioni aziendali.
                                        </p>
                                    </div>
                                    <ul className="mt-5 space-y-1.5 text-xs text-slate-500">
                                        <li>• Documenti personali sempre disponibili</li>
                                        <li>• Invia certificati e richieste all'HR</li>
                                        <li>• Aggiorna i tuoi dati di contatto</li>
                                    </ul>
                                    <div className="mt-6 inline-flex items-center gap-2 font-medium text-[#376DB3] group-hover:gap-3 transition-all">
                                        Entra come dipendente
                                        <ArrowRight className="h-4 w-4" />
                                    </div>
                                </div>
                            </Link>

                            {/* Admin card */}
                            <Link
                                to="/login?role=admin"
                                className="group relative overflow-hidden rounded-2xl bg-[#15498F] shadow-2xl ring-1 ring-white/15 transition-all hover:-translate-y-1 hover:shadow-[0_20px_60px_-10px_rgba(21,73,143,0.6)]"
                                data-testid="role-card-admin"
                            >
                                <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#5BAEDB] to-[#7BC4E8]" />
                                {/* subtle grid pattern */}
                                <div
                                    className="absolute inset-0 opacity-[0.08]"
                                    style={{
                                        backgroundImage:
                                            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                                        backgroundSize: "24px 24px",
                                    }}
                                />
                                <div className="relative p-7 sm:p-8">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/15 text-white transition-colors group-hover:bg-white group-hover:text-[#15498F]">
                                        <ShieldCheck className="h-7 w-7" />
                                    </div>
                                    <div className="mt-5">
                                        <div className="text-[10px] font-semibold uppercase tracking-widest text-[#5BAEDB]">
                                            Gestionale HR
                                        </div>
                                        <h2 className="mt-1 font-display text-2xl font-bold text-white">
                                            Sono un amministratore
                                        </h2>
                                        <p className="mt-2 text-sm text-blue-100/80">
                                            Accedi al pannello di controllo per gestire dipendenti,
                                            documenti, tesserini e conformità HR.
                                        </p>
                                    </div>
                                    <ul className="mt-5 space-y-1.5 text-xs text-blue-100/70">
                                        <li>• Gestione completa anagrafica e contratti</li>
                                        <li>• Upload massivo documenti</li>
                                        <li>• Conformità HR e tesserini identificativi</li>
                                    </ul>
                                    <div className="mt-6 inline-flex items-center gap-2 font-medium text-white group-hover:gap-3 transition-all">
                                        Entra come amministratore
                                        <ArrowRight className="h-4 w-4" />
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>
                </main>

                {/* Footer */}
                <footer className="border-t border-white/10 px-6 py-4 text-center text-xs text-white/70 sm:px-10">
                    {company.ragione_sociale} · {company.sede} ·{" "}
                    <a href="tel:+39099375014" className="font-medium text-white hover:underline">
                        099.375014
                    </a>{" "}
                    ·{" "}
                    <a href="tel:+393481966463" className="font-medium text-white hover:underline">
                        348.1966463
                    </a>
                </footer>
            </div>
        </div>
    );
}
